<template>
  <!-- The sheet is a flex sibling below #mainContent, so when it collapses to
       just the 18px arrow the arrow sits BELOW the canvas, on the page
       background — which read as a full-width white bar either side of the
       arrow. Pulling the collapsed sheet up by its own height slides it over
       the canvas instead, so the arrow floats on the grid with nothing beside
       it. 'full' and 'tools' stay in normal flow so the canvas genuinely
       shrinks for them. -->
  <div
    class="mobile-tools-container w-full xl:hidden select-none bg-transparent"
    :class="`sheet--${sheetState}`"
  >
    <!-- Sheet assembly, using the same 300ms ease-out as the color picker's
         drawer-assembly. The arrow is the trigger handle and stays pinned at the
         top; the collapsible row below it holds the toolbar, shape panel and
         footer.

         Three states, stepped down by a swipe on the body:
           'full'     — arrow + edit toolbar + shapes strip + footer
           'tools'    — arrow only, maximum canvas
           'collapsed'— arrow only, grid fully visible
         The slot releases its space only AFTER the content has slid out, so
         the panel visibly slides away instead of the content dropping into
         a gap that closed underneath it. -->
    <div
      class="sheet-assembly w-full"
      @touchstart.passive="onSheetTouchStart"
      @touchmove.passive="onSheetTouchMove"
      @touchend.passive="onSheetTouchEnd"
      @touchcancel.passive="onSheetTouchCancel"
    >
      <!-- Trigger Arrow (Figma 7671:240258 — Group 1500 of "Mobile · Boxes tab").
           The navy drawer handle (Figma Union 7520:165933) rotated 90° CW, lying
           flat as a 117×18 bar with the white Vector 364 chevron (7520:165934)
           pointing up — the same asset the color picker rotates for its own
           handle. Geometry exported verbatim from the node. The button is
           transparent and only as wide as the arrow (like .drawer-handle in
           ColorDrawer.vue) so no white bar shows on either side of it; the
           sheet's own white background supplies the surrounding surface. The
           chevron does NOT flip, matching the color drawer, whose handle art
           is fixed-orientation. -->
      <div
        class="sheet-arrow-row relative w-full h-[18px] shrink-0 flex items-center justify-center bg-transparent -mt-[18px] mb-0 z-[2]"
      >
        <button
          type="button"
          class="sheet-arrow relative w-[117px] h-[18px] flex items-center justify-center bg-transparent p-0 shrink-0 cursor-pointer focus:outline-none"
          :aria-expanded="sheetState === 'full'"
          aria-label="Toggle tools panel"
          @click="onArrowClick"
        >
          <svg
            class="sheet-arrow-icon block w-[117px] h-[18px]"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 117 18"
            fill="none"
            aria-hidden="true"
            v-html="sheetChevronInner"
          />
        </button>
      </div>

      <!-- Collapsible row: toolbar, shape panel and footer.

           The slot's HEIGHT is animated (height transition, same 300ms
           ease-out as the color drawer's transform), so #mainContent grows
           back in step with the panel sliding away. The bands are anchored
           to the BOTTOM of the slot, so as the slot shrinks they are pushed
           down and out of the clip box - the content slides off the bottom
           edge of the screen rather than dropping into a gap.

           The slot is anchored to the viewport bottom (its parent is the
           bottom of the flex column), so shrinking it does not move the
           arrow row, which is a sibling above it. -->
      <div
        class="sheet-slot overflow-hidden bg-transparent"
        :class="`sheet-slot--${sheetState}`"
      >
        <!-- Two independently collapsible bands so the sheet can stop at an
             intermediate step:

               tools     — edit toolbar only (shapes strip + footer hidden)
               collapsed — nothing but the arrow

             The stack is anchored to the slot's top edge, so it travels with
             the slot as the height transition runs and slides down out of the
             clipped window while the canvas grows behind it. -->
        <div class="sheet-band-stack">
          <!-- The tools band collapses by height (not v-show) when the tile
               rotation card opens, so it slides away in step with the picker
               band growing into its space instead of snapping out of
               existence (see .sheet-band--tools-shrunk below). -->
          <div
            class="sheet-band sheet-band--tools"
            :class="[toolsBandVisible ? 'sheet-band--shown' : 'sheet-band--hidden',
                     showEditToolbar ? '' : 'sheet-band--tools-shrunk']"
          >
            <div
              ref="sheetBodyRef"
              class="sheet-body"
            >
              <!-- Floating Horizontal Edit Toolbar (Undo, Redo, Delete, Copy, Cut, Paste).
                   Hidden while a tile's rotation card is open — that card replaces
                   the whole section below the arrow (Figma 7675:162616). -->
              <ToolsMobileToolsHorizontalToolbar />
            </div>
          </div>

          <!-- The shape strip and the footer are separate bands so the footer
               can stay pinned to the bottom of the viewport while the strip
               above it changes height (the rotation card is taller than the
               strip, and the footer must not move when it appears). -->
          <div
            class="sheet-band sheet-band--picker"
            :class="[
              pickerBandVisible ? 'sheet-band--shown' : 'sheet-band--hidden',
              rotationCardOpen ? 'sheet-band--picker-rotation' : '',
            ]"
          >
            <!-- Modern Shape Panel with Currently Selected banner, Section Tabs, Sticky Scroller, Depth Toggle -->
            <ToolsMobileToolsShapePanel />
          </div>

          <div
            class="sheet-band sheet-band--footer"
            :class="[pickerBandVisible ? 'sheet-band--shown' : 'sheet-band--hidden',
                     sheetState === 'full' ? 'sheet-band--footer-visible' : 'sheet-band--footer-hidden']"
          >
            <!-- Modern Mobile Footer Bar with View Tiles / Price Pill and Add to Cart Button -->
            <ToolsMobileToolsFooterBar />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import "shepherd.js/dist/css/shepherd.css"
import { useShepherd } from "vue-shepherd"

// Inlined rather than referenced with <img src>. Vite's dev asset pipeline
// rewrites the path to `/&/new-studio/...` (its query separator leaking into
// the URL), which 404s and renders a broken-image icon. Inlining sidesteps the
// asset resolver entirely and matches how ShapePanel loads its tile SVGs.
const svgRaw = import.meta.glob('../../../public/new-studio/mobile/*.svg', {
  eager: true,
  query: '?raw',
  import: 'default',
}) as Record<string, string>

// Inner markup of the arrow SVG (everything between <svg> and </svg>).
const sheetChevronInner = (() => {
  const raw = svgRaw['../../../public/new-studio/mobile/sheet-chevron.svg'] || ''
  return raw.match(/<svg[^>]*>([\s\S]*?)<\/svg>/)?.[1] ?? ''
})()

// Bottom section state.
//
//   'full'      arrow + edit toolbar + shapes strip + footer (default)
//   'tools'     arrow + edit toolbar, shapes strip and footer hidden
//   'collapsed' arrow only, grid fully visible
//
// A swipe down on the sheet steps down one level: full -> tools -> collapsed.
// A swipe up steps back up. The arrow button cycles the same way.
//
// The sheet is two independently collapsible bands so it can stop at the
// intermediate step. The slot's height transition (CSS) does the motion and
// the canvas grows back in step with it, so the panel is always seen
// travelling rather than popping out of existence.
type SheetState = 'full' | 'tools' | 'collapsed'

// True while ShapePanel has swapped its strip for the tile-rotation card.
// Figma 7675:162616 replaces the whole section below the arrow with that
// card, so the edit toolbar and the shape strip step aside. ShapePanel owns
// the state; this mirrors it for the toolbar, which lives in this component.
const showEditToolbar = useState<boolean>('mobile-show-edit-toolbar', () => true)
// True while the tile-rotation card is up. The card is taller than the shape
// strip, so the picker band grows to take the space the hidden edit toolbar
// frees up, and the footer stays pinned to the bottom.
const rotationCardOpen = useState<boolean>('mobile-rotation-card-open', () => false)
const sheetState = ref<SheetState>('full')

// Mirrored to the shell (StudioMobile.vue) so the bottom section's wrapper
// stops painting white when the sheet is collapsed - the canvas should show
// through to the bottom of the screen.
const sheetCollapsed = useState<boolean>('mobile-sheet-collapsed', () => false)
watch(sheetState, (state) => { sheetCollapsed.value = state === 'collapsed' }, { immediate: true })

// Band 1 — the edit toolbar. Stays put through the first collapse.
const toolsBandVisible = ref(true)
// Band 2 — the shapes strip and footer. This is what the first swipe hides.
const pickerBandVisible = ref(true)

const sheetBodyRef = ref<HTMLElement | null>(null)

const isFull = () => sheetState.value === 'full'
const isCollapsed = () => sheetState.value === 'collapsed'

// Step down one level. The slot's height transition (CSS, 300ms ease-out)
// drives the motion: the band that is leaving is hidden immediately so it
// stops intercepting touches, and the shrinking slot pushes the remaining
// content down and out of the clip box.
const stepDown = () => {
  if (isFull()) {
    // full -> tools: hide the shapes strip + footer, keep the edit toolbar.
    pickerBandVisible.value = false
    sheetState.value = 'tools'
    return
  }
  if (sheetState.value === 'tools') {
    // tools -> collapsed: hide the edit toolbar too.
    toolsBandVisible.value = false
    sheetState.value = 'collapsed'
  }
}

// Arrow tap: steps down one level at a time (full -> tools -> collapsed),
// and when fully collapsed, expands back up to full.
// The arrow's click handler. Swallows the click that follows a swipe that
// started on the arrow, since the swipe already moved the sheet.
const onArrowClick = () => {
  // Swallow the click the browser synthesises for a swipe that began on this
  // button, otherwise it would immediately undo the swipe.
  if (Date.now() - lastArrowSwipeAt.value < ARROW_CLICK_GRACE_MS) {
    lastArrowSwipeAt.value = 0
    return
  }
  toggleSheet()
}

const toggleSheet = () => {
  if (sheetState.value === 'collapsed') {
    expandSheet()
  } else {
    stepDown()
  }
}

// Restore both bands. The bands are made visible before the slot grows, so
// the content is in place for the height transition to reveal.
const expandSheet = () => {
  if (isFull()) return
  toolsBandVisible.value = true
  pickerBandVisible.value = true
  sheetState.value = 'full'
}

// Step up one level: collapsed -> tools, or tools -> full.
const stepUp = () => {
  if (isCollapsed()) {
    toolsBandVisible.value = true
    sheetState.value = 'tools'
    return
  }
  if (sheetState.value === 'tools') {
    expandSheet()
  }
}

// --- Swipe gestures (Up / Down) ---------------------------------------------
// Captures vertical swipes anywhere on the sheet assembly (including the
// arrow handle). Ignores horizontal swipes on scrolling strips (tiles / tabs).
const SWIPE_THRESHOLD_PX = 32
const SWIPE_MAX_TIME_MS = 600
const touchStart = ref<{
  x: number
  y: number
  t: number
  isScrollArea: boolean
  onArrow: boolean
} | null>(null)

// True once a gesture has moved far enough to count as a swipe. A swipe that
// starts on the arrow button also fires that button's click on touchend, so
// the click has to be suppressed or the two would cancel each other out.
const gestureMoved = ref(false)
// Timestamp of the last swipe that began on the arrow button. The browser
// may synthesise a click for that same gesture, which would undo the swipe,
// so the click is swallowed - but only if it arrives immediately afterwards.
// A time window is used rather than a flag because a drag often produces no
// click at all, and a sticky flag would then swallow the user's next real
// tap.
const lastArrowSwipeAt = ref(0)
const ARROW_CLICK_GRACE_MS = 250

const onSheetTouchStart = (e: TouchEvent) => {
  const t = e.touches[0]
  if (!t) return
  const target = e.target as HTMLElement | null
  const isScrollArea = Boolean(
    target?.closest('.shape-scroller') ||
    target?.closest('.section-tabs-strip')
  )
  gestureMoved.value = false
  touchStart.value = {
    x: t.clientX,
    y: t.clientY,
    t: Date.now(),
    isScrollArea,
    onArrow: Boolean(target?.closest('.sheet-arrow')),
  }
}

const onSheetTouchMove = (e: TouchEvent) => {
  const start = touchStart.value
  if (!start || gestureMoved.value) return
  const t = e.touches[0]
  if (!t) return
  if (Math.abs(t.clientY - start.y) >= SWIPE_THRESHOLD_PX ||
      Math.abs(t.clientX - start.x) >= SWIPE_THRESHOLD_PX) {
    gestureMoved.value = true
  }
}

const onSheetTouchCancel = () => {
  touchStart.value = null
  gestureMoved.value = false
}

const onSheetTouchEnd = (e: TouchEvent) => {
  const start = touchStart.value
  touchStart.value = null
  if (!start) return

  const t = e.changedTouches[0]
  if (!t) return
  const dx = t.clientX - start.x
  const dy = t.clientY - start.y
  const elapsed = Date.now() - start.t

  // A drag that moved past the threshold is a swipe, not a tap. Remember that
  // it happened so the click the browser may synthesise for it is swallowed.
  if (gestureMoved.value && start.onArrow) {
    lastArrowSwipeAt.value = Date.now()
  }

  // If the gesture started on a horizontally scrollable strip and the
  // movement was primarily horizontal, let native horizontal scroll handle it.
  if (start.isScrollArea && Math.abs(dx) > Math.abs(dy)) {
    return
  }

  // Must be primarily vertical and fast enough to be a flick/swipe.
  if (Math.abs(dy) <= Math.abs(dx) || elapsed > SWIPE_MAX_TIME_MS) {
    return
  }

  // Swipe DOWN: step down (full -> tools -> collapsed)
  if (dy >= SWIPE_THRESHOLD_PX) {
    stepDown()
    return
  }

  // Swipe UP: step up or expand
  if (dy <= -SWIPE_THRESHOLD_PX) {
    // A larger upward drag from collapsed pulls everything open to full
    if (isCollapsed() && dy <= -75) {
      expandSheet()
    } else {
      stepUp()
    }
  }
}

const tour = useShepherd({
  useModalOverlay: true,
  defaultStepOptions: {
    cancelIcon: {
      enabled: true,
    },
    scrollTo: { behavior: "smooth", block: "center" },
  },
})

const addTourInitialSteps = () => {
  const defaultButtons = [
    {
      action: tour.back,
      classes: "shepherd-button-secondary",
      text: "Back",
    },
    {
      action: tour.next,
      text: "Next",
    },
  ]

  tour.addStep({
    title: "Adding a tile",
    text: "Tap on the canvas to add new tiles",
    classes: "shepherdTopMargin",
    attachTo: {
      element: "#mainContent",
      on: "top",
    },
    buttons: [
      {
        action: tour.next,
        text: "Next",
      },
    ],
  })

  tour.addStep({
    title: "Select Tiles",
    text: "Tap on the tile to select it, after selecting you can adjust color, you can also delete and move tiles around with this tool.",
    classes: "shepherdTopMargin",
    attachTo: {
      element: "#mainContent",
      on: "top",
    },
    buttons: defaultButtons,
  })

  tour.addStep({
    title: "Move / Zoom",
    text: "You can zoom & move / pan the grid pinching with your fingers to zoom.",
    classes: "shepherdTopMargin",
    attachTo: {
      element: "#mainContent",
      on: "top",
    },
    buttons: defaultButtons,
  })

  tour.addStep({
    title: "Undo / Redo",
    text: "Undo / Redo mistakes with these tools.",
    classes: "shepherdTopMargin",
    attachTo: {
      element: ".mobile-edit-toolbar",
      on: "bottom",
    },
    buttons: defaultButtons,
  })

  tour.addStep({
    title: "Congratulations",
    text: "You've reached the end of the tutorial! We hope you enjoy the new Mobile Design Studio!",
  })
}

onMounted(() => {
  addTourInitialSteps()
  document.addEventListener("mobileTour:start", tour.start)
})
</script>

<style scoped>
.mobile-tools-container {
  position: relative;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.06);
}

/* Collapsible sheet. The slot's height animates over 300ms ease-out, the
   same duration and easing as the color picker's drawer-assembly transform.
   Because #mainContent is flex-1, the sheet's real layout height shrinking
   hands the reclaimed space back to the grid continuously, so the canvas
   grows in step with the panel sliding away.

   The bands are absolutely positioned against the BOTTOM of the slot. As the
   slot shrinks, they are pushed down and clipped away by the slot's
   overflow, so the content slides off the bottom edge of the screen instead
   of dropping into a gap that closed underneath it. */
.sheet-slot {
  position: relative;
  overflow: hidden;
  transition: height 300ms ease-out;
}

/* The container is transparent: the sheet's rounded top corners must show
   the canvas grey behind them, and the white that runs past the bottom of
   the sheet lives on the tools wrapper (StudioMobile.vue, as the
   .mobile-sheet-surface ::after).

   iOS Safari lays the page out taller than the area on screen and floats
   its bottom bar over the overflow, so the region behind the bar has to be
   the sheet's white - otherwise the canvas grey showed through the bar as a
   grey band under "Add to Cart". That region sits OUTSIDE the studio shell
   entirely, so the root white in StudioMobile.vue is what actually fills
   it; the ::after covers the part of it the shell does reach. */
.mobile-tools-container {
  position: relative;
}

/* The slot is a FIXED height in every state so the bottom section never
   changes size - switching tabs, collapsing, or swapping the strip for the
   rotation card all keep it identical. The rotation card is taller than the
   strip, so ShapePanel constrains it to fit this budget rather than growing
   the section (see .rotation-card-wrap there). */
/* On iPhone the footer carries a safe-area-inset-bottom padding so it
   extends behind the home indicator / Safari's bottom bar. Without adding
   the same inset to the slot and footer band, that padding is clipped by
   the slot's overflow:hidden and the canvas grey shows in the strip
   behind the browser bar. */
.sheet-slot--full {
  height: calc(288px + env(safe-area-inset-bottom, 0px));
  min-height: 288px;
}
.sheet-slot--tools { height: 55px; }
/* The 18px arrow row is a sibling ABOVE the slot, so the slot itself is 0
   when collapsed - otherwise the two stack to 36px. */
.sheet-slot--collapsed { height: 0; }

/* The stack is anchored to the TOP of the slot and its bands sit in normal
   flow, in DOM order: the edit toolbar first (directly under the arrow),
   then the shape strip, then the footer.

   Top-anchoring is what makes the collapse steps read correctly. The slot
   is the viewport window onto the stack: at 288px the whole stack shows,
   at 55px only the toolbar (the first 55px) is inside the window, and at 0
   nothing is. Because the slot's top edge is the stack's top edge, the
   stack slides down with the window as it shrinks, so the content travels
   off the bottom edge of the screen in step with the canvas growing
   behind it - no per-frame JavaScript and no transform bookkeeping.

   The stack is as tall as the SLOT, not a fixed 288px, and the footer band
   is pinned to the BOTTOM of it. The slot's height is the sheet's height,
   which on iOS Safari is the VISIBLE viewport - shorter than the design's
   288px when the browser bar is showing. With a fixed-height stack the
   footer sat at 288px down the stack and was clipped off the bottom of the
   screen, leaving the canvas grey showing underneath it. */
.sheet-band-stack {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  /* Above the white backdrop that runs past the bottom of the sheet (the
     .mobile-sheet-surface ::after in StudioMobile.vue, z-index 0) so the
     bands are never painted over by it. */
  z-index: 1;
  display: flex;
  flex-direction: column;
  /* Bands are laid out from the TOP of the stack and overflow past the
     bottom, which the slot's overflow:hidden clips. Anchoring them to the
     bottom instead (justify-content: flex-end) made them overflow the
     top of the slot when the stack was shorter than they were - the footer
     band then showed above the slot's top edge and stayed visible. */
  justify-content: flex-start;
}

/* Bands stack in DOM order: the edit toolbar first (directly under the
   arrow), then the shape strip, then the footer at the very bottom. */
.sheet-band {
  width: 100%;
}

/* The edit toolbar is 55px. When the tile-rotation card opens the band
   collapses to 0 so the picker band can grow into its space; the height
   transition makes it slide away instead of popping out. */
.sheet-band--tools {
  height: 55px;
  transition: height 300ms ease-out;
}
.sheet-band--tools-shrunk {
  height: 0;
  overflow: hidden;
}

/* The shape strip band is 168px in the strip view. In the rotation view the
   edit toolbar is hidden, so the card grows into that space (288 - 65 for
   the footer = 223px) and the footer still sits at the bottom. The 55px
   the tools band gives up and the 55px this band takes on are the same
   pixels, so both transitions run on one track: the card's header slides
   up into the toolbar's place while its rotation tiles arrive where the
   shape strip was. */
.sheet-band--picker {
  height: 168px;
  transition: height 300ms ease-out;
}
.sheet-band--picker-rotation { height: 223px; }

/* The footer is the last band, 65px, pinned to the BOTTOM of the stack so it
   always sits at the bottom of the visible viewport. It is absolutely
   positioned so it does not push the other bands around.

   It is only VISIBLE in the 'full' state. In the 'tools' state the slot is
   55px tall but the footer is 65px, so it overflowed the slot's top edge
   and the part above the clip stayed on screen - a white strip over the
   canvas. */
/* The footer band sizes to the footer's own height (which already carries
   the safe-area padding), and is pinned to the bottom of the stack. Using a
   fixed height here clipped the footer's safe-area padding on iPhone,
   leaving the canvas grey showing in the strip behind Safari's bottom bar. */
.sheet-band--footer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: auto;
}

.sheet-band--footer-hidden {
  visibility: hidden;
}

/* A hidden band keeps its space in the flow: it is pushed down and clipped
   away by the shrinking slot rather than vanishing in place. */
.sheet-band--hidden {
  pointer-events: none;
}

/* Band 1 - edit toolbar (55px). This is the white card directly under the
   arrow, so it carries the sheet's rounded top corners and the upward
   drop shadow (Figma 7671:240258 "Component 3": rgba(20,20,21,0.10),
   offset y -7.38px, blur 12.92px). The arrow overlaps its top edge,
   matching the design.

   The radius comes from --sheet-corner-radius (assets/style.scss), which
   is 28px. The Figma spec for this card says 18.46px; the larger radius is
   a deliberate visual change, so the four places that must agree on it
   (this card, the toolbar wrap, the rotation card and the corner strip)
   all read the same variable rather than repeating a literal. */
.sheet-band--tools .sheet-body {
  height: 55px;
  background: #FFFFFF;
  border-radius: var(--sheet-corner-radius) var(--sheet-corner-radius) 0 0;
  box-shadow: 0 -7px 13px rgba(20, 20, 21, 0.1);
  overflow: hidden;
}

.sheet-body {
  touch-action: pan-x;
}

/* The white chevron vector inside the sheet arrow button points in the
   direction the sheet will move. The sheet loads fully expanded, so the
   chevron starts pointing DOWN (tap to collapse it); when the sheet is
   collapsed it flips to point UP (tap to pull it back open). */
.sheet-arrow-icon :deep(.sheet-chevron-vector) {
  transform-origin: 58px 8.66px;
  transition: transform 300ms ease;
  transform: rotate(180deg);
}

.sheet--collapsed .sheet-arrow-icon :deep(.sheet-chevron-vector) {
  transform: rotate(0deg);
}

@media (prefers-reduced-motion: reduce) {
  .sheet-slot {
    transition: none;
  }
  .sheet-band--tools,
  .sheet-band--picker {
    transition: none;
  }
  .sheet-arrow-icon :deep(.sheet-chevron-vector) {
    transition: none;
  }
}
</style>
